<template>
  <div class="risk-analysis-asset-table">
    <v-data-table
      :headers="columns"
      :items="filteredValue"
      item-key="assetId"
      dense
      :options="options"
      class="itus-table elevation-3"
      @update:options="$emit('sort-changed', $event)"
      @click:row="edit"
      :loading="loading"
      loading-text="Loading..."
      no-results-text="No results found."
      no-data-text="No results found."
      hide-default-footer
      :page.sync="page"
      @page-count="pageCount = $event"
    >
      <template v-slot:top>
        <FilterBar flat :label="assetTableLabel">
          <template v-slot:toolbar v-if="!readonly && mode !== 'scenario'">
            <v-btn icon small color="secondary" class="mr-1" @click.stop="addAsset" :disabled="!canAddAsset">
              <v-icon small>fa-plus</v-icon>
            </v-btn>
            <v-menu v-if="environmentMode !== 'apm'" left offset-y v-model="importMenu">
              <template v-slot:activator="{ on }">
                <v-btn icon small color="secondary" class="mr-1" v-on="on">
                  <v-icon small>fa-ellipsis-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(action, index) in listActions"
                  :key="index"
                  :disabled="action.disabled ? action.disabled : false"
                  :href="action.href"
                  :download="action.download"
                  @click.stop="action.handler ? action.handler() : emptyHandler()"
                >
                  <v-list-item-icon>
                    <v-icon small>{{ action.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ action.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <template v-slot:default>
            <v-container fluid>
              <v-row align="center" justify="center">
                <v-col class="d-flex justify-start filter-column">
                  <v-select
                    label="Improve"
                    :items="['true', 'false']"
                    :value="filterDef.improve"
                    @input="improveChanged"
                    clearable
                    @click:clear="clearImprove"
                    hide-details
                    ref="improveFilter"
                  >
                  </v-select>
                </v-col>
                <v-col class="d-flex justify-start filter-column">
                  <v-text-field
                    label="Name"
                    :value="filterDef.name"
                    @input="nameChanged"
                    clearable
                    hide-details
                    ref="nameFilter"
                  >
                  </v-text-field>
                </v-col>
                <v-col class="d-flex justify-start filter-column">
                  <v-select
                    label="Taxonomy"
                    item-text="caption"
                    item-value="value"
                    :items="classes"
                    :value="filterDef.class"
                    @input="classChanged"
                    clearable
                    @click:clear="clearClass"
                    hide-details
                    ref="classFilter"
                  >
                  </v-select>
                </v-col>
                <v-col class="d-flex justify-start filter-column">
                  <v-select
                    label="Characteristic life"
                    :items="['Worse than benchmark', 'Better than benchmark', 'No benchmark available']"
                    :value="filterDef.mtbf"
                    @input="mtbfChanged"
                    clearable
                    @click:clear="clearMtbf"
                    hide-details
                    ref="mtbfFilter"
                  >
                  </v-select>
                </v-col>
                <v-col class="d-flex justify-start filter-column">
                  <v-select
                    label="Opportunity"
                    :items="['Over opportunity threshold', 'Under opportunity threshold']"
                    :value="filterDef.opportunity"
                    @input="opportunityChanged"
                    clearable
                    @click:clear="clearOpportunity"
                    hide-details
                    ref="opportunityFilter"
                  >
                  </v-select>
                </v-col>
                <v-col class="justify-center filter-button-column">
                  <v-btn color="secondary" text @click.native.stop.prevent="clearFilter">Clear</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </template>
        </FilterBar>
      </template>

      <template v-slot:[`item.improve`]="{ item }">
        <v-tooltip open-delay="500" color="white" right>
          <template v-slot:activator="{ on, attrs }">
            <v-simple-checkbox
              @input="toggleImprove(item)"
              v-bind="attrs"
              v-on="on"
              :value="item.improve"
              :ripple="false"
              :disabled="readonly || mode === 'scenario'"
            ></v-simple-checkbox>
          </template>
          <span>{{ improveText(item) }}</span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.assetName`]="{ item }">
        <template v-if="item.twinId !== null && item.twinId !== undefined && item.twinId !== ''">
          <v-tooltip open-delay="500" right color="white">
            <template v-slot:activator="{ on, attrs }">
              <v-icon :color="twinIconColor(item)" left small v-bind="attrs" v-on="on">{{ twinIcon }}</v-icon>
            </template>
            <span v-html="twinIconTooltip(item)"></span>
          </v-tooltip>
        </template>
        <span :class="assetNameClass(item)">{{ item.assetName }}</span>
        <v-tooltip open-delay="500" color="white" v-if="hasChanges(item)" right>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" right color="info" x-small>fa-solid fa-circle</v-icon>
          </template>
          <span>{{ changeSummaryText(item) }}</span>
        </v-tooltip>
      </template>

      <template v-slot:[`item.probabilityFailureDistributionScale`]="{ item }">
        <template v-if="mtbfDisplayText(item) || buildLibraryComparisonText(item)">
          <v-tooltip open-delay="500" left color="white">
            <template v-slot:activator="{ on, attrs }">
              <v-icon :color="libraryComparisonColor(item)" small class="mr-2" v-bind="attrs" v-on="on"
                >fa-solid fa-lightbulb-exclamation</v-icon
              >
            </template>
            <span v-html="buildLibraryComparisonText(item)"></span>
          </v-tooltip>
          <span v-html="mtbfDisplayText(item)"></span>
        </template>
      </template>

      <template v-slot:[`item.opportunity`]="{ item }">
        <template
          v-if="item.probabilityValue !== null && item.probabilityValue !== undefined && !isNaN(item.probabilityValue)"
        >
          <v-tooltip open-delay="500" left color="white">
            <template v-slot:activator="{ on, attrs }">
              <v-icon :color="opportunityColor(item)" small v-bind="attrs" v-on="on">fa-regular fa-dollar-sign</v-icon>
            </template>
            <span v-html="opportunityTooltip(item)"></span>
          </v-tooltip>
        </template>
      </template>

      <template v-slot:[`item.probabilityValue`]="{ item }">
        <template
          v-if="item.probabilityValue !== null && item.probabilityValue !== undefined && !isNaN(item.probabilityValue)"
        >
          <strong v-if="$vuetify.breakpoint.xs">{{ calcPof(item.probabilityValue) }}%</strong>
          <v-progress-linear v-else :value="calcPof(item.probabilityValue)" height="20" color="accent">
            <template v-slot="{ value }">
              <strong>{{ value }}%</strong>
            </template>
          </v-progress-linear>
        </template>
        <v-icon v-else small class="empty-icon"> fa-regular fa-empty-set </v-icon>
      </template>

      <template v-slot:[`item.consequenceValue`]="{ item }">
        <GradientValue
          v-if="item.consequenceValue !== null && item.consequenceValue !== undefined && !isNaN(item.consequenceValue)"
          class="font-weight-bold"
          :value="item.consequenceValue"
          :startColor="consequenceGradientStart"
          :endColor="consequenceGradientEnd"
          :endValue="maxConsequence"
        ></GradientValue>
        <v-icon v-else small class="empty-icon"> fa-regular fa-empty-set </v-icon>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu :ref="item.assetId" class="asset-actions" left offset-y @input="menuInput(item)">
          <template v-slot:activator="{ on }">
            <v-btn icon x-small color="secondary" v-on="on">
              <v-icon x-small>fa-ellipsis-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(action, index) in assetActions"
              :key="index"
              :disabled="action.disabled ? action.disabled(item) : false"
              @click="action.handler(item)"
            >
              <v-list-item-icon>
                <v-icon small>{{ action.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ action.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:[`item.risk`]="{ item }">
        <GradientValue
          v-if="item.risk !== null && item.risk !== undefined && !isNaN(item.risk)"
          class="font-weight-bold"
          :value="item.risk"
          :startColor="riskGradientStart"
          :endColor="riskGradientEnd"
          :endValue="maxRisk"
        ></GradientValue>
        <v-icon v-else small class="empty-icon"> fa-regular fa-empty-set </v-icon>
      </template>
    </v-data-table>
    <div class="text-center pt-2">
      <v-pagination v-model="page" :length="pageCount" :total-visible="maxPagingSize"></v-pagination>
    </div>
    <v-file-input
      ref="fileInput"
      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
      @change="importFile"
    ></v-file-input>
    <WarningDialog
      ref="warningDialog"
      :allowSuppression="true"
      title="May invalidate what-ifs"
      text="Are you sure you want to make this change which may invalidate existing what-if scenarios?"
    ></WarningDialog>
  </div>
</template>
<script>
import { GradientValue, FilterBar, WarningDialog, Time, Sort, TWIN_ICON, ASSET_ICON, EDIT_ICON } from '@itus/ui.common'
import { debounce } from 'lodash'
import { Duration, DateTime } from 'luxon'

export default {
  components: {
    FilterBar,
    GradientValue,
    WarningDialog
  },
  name: 'RiskAnalysisAssetTable',
  props: {
    value: {
      type: Array,
      description: 'Array of assets'
    },
    analysis: {
      type: Object,
      description: 'The risk analysis'
    },
    missionParametersDirty: {
      type: Boolean,
      description: 'Flags whether the start date or mission time has changed but not been saved yet.',
      default: false
    },
    loading: {
      type: Boolean,
      description: 'Table loading indicator',
      default: false
    },
    probabilityThreshold: {
      type: Number,
      description: 'Probability of failure opportunity threshold',
      default: 50
    },
    canAddAsset: {
      type: Boolean,
      description: 'Controls add menu option',
      default: true
    },
    canDeleteAsset: {
      type: Boolean,
      description: 'Controls delete menu option',
      default: true
    },
    assetsPerAnalysis: {
      type: Number,
      description: 'The max number of assets that can be in the analysis',
      default: 100
    },
    mode: {
      type: String,
      description: 'Valid modes are analysis and scenario',
      default: 'analysis'
    },
    comparisonValue: {
      type: Array,
      description: 'Array of assets for comparison'
    },
    taxonomyMap: {
      type: Object,
      description: 'state object for accessing taxonomy info'
    },
    taxonomyList: {
      type: Array,
      description: 'state object that provides a flattened taxonomy list'
    },
    scenarioInvalidationConfirmed: {
      type: Boolean,
      description: 'state object for tracking when the user confirms invalidation of scenarios'
    },
    scenarios: {
      type: Array,
      description: 'An array of scenario objects from state'
    },
    assetDialog: {
      type: Object,
      description: 'the dialog object to be shown when adding assets'
    },
    editAssetDialog: {
      type: Object,
      description: 'the dialog object to be shown when editing assets specifically in APM'
    },
    analysisEndDate: {
      type: DateTime,
      description: 'the end date of the analysis',
      default: null
    },
    timeZone: {
      type: String,
      description: 'If provided, is used to localize date time info',
      default: 'local'
    },
    readonly: Boolean
  },
  data() {
    return {
      page: 1,
      pageCount: 0,
      maxPagingSize: 6,
      columns: [
        { text: 'Improve', value: 'improve' },
        { text: 'Name', value: 'assetName' },
        { text: 'Taxonomy', value: 'assetClassLabel' },
        {
          text: 'Characteristic Life',
          value: 'probabilityFailureDistributionScale',
          cellClass: 'text-no-wrap',
          sort: (a, b) => {
            if (!a && !b) return 0

            if (a && !b) return 1

            if (b && !a) return -1

            let val1 = Duration.fromISO(a).toMillis()
            let val2 = Duration.fromISO(b).toMillis()
            if (val1 > val2) return 1
            else if (val2 > val1) return -1
            else return 0
          }
        },
        { text: 'Opp.', value: 'opportunity' },
        { text: 'Probability', value: 'probabilityValue', sort: Sort.sortHandleNull },
        { text: 'Cost of Failure', value: 'consequenceValue', sort: Sort.sortHandleNull },
        { text: 'Risk', value: 'risk', sort: Sort.sortHandleNull },
        { value: 'actions', align: 'end', width: 40, sortable: false }
      ],
      consequenceGradientStart: 'rgb(255,248,225)',
      consequenceGradientEnd: 'rgb(255, 160, 0)',
      riskGradientStart: 'rgb(254,252,252)',
      riskGradientEnd: 'rgb(183,28,28)',
      comparisonValueDictionary: null,
      filterDef: {
        improve: undefined,
        name: '',
        class: undefined,
        mtbf: undefined,
        opportunity: undefined
      },
      options: {
        page: 1,
        itemsPerPage: 100,
        sortBy: ['risk'],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: false
      },
      filteredValue: [],
      importMenu: false,
      suppress: false,
      selectedAsset: null,
      twinIcon: TWIN_ICON
    }
  },
  watch: {
    comparisonValue: {
      handler(value) {
        if (!value || this.mode !== 'scenario' || value.length == 0) {
          this.comparisonValueDictionary = null
          return
        }

        let dict = {}
        for (let v of value) {
          dict[v.assetId] = v
        }

        this.comparisonValueDictionary = dict
      },
      immediate: true
    },
    value: {
      handler() {
        if (this.options.page !== 1) this.options.page = 1 // resetting to first page will trigger filtered re-load of grid

        this.debouncedFilterAssets()
      },
      deep: true
    },
    filterDef: {
      handler() {
        if (this.options.page !== 1) this.options.page = 1 // resetting to first page will trigger filtered re-load of grid

        this.debouncedFilterAssets()
      },
      deep: true
    }
  },
  computed: {
    environmentMode() {
      return this.editAssetDialog ? 'apm' : 'ara'
    },
    listActions() {
      return this.mode === 'scenario' || this.environmentMode === 'apm'
        ? []
        : [
            { title: 'Import', icon: 'fa-file-import', handler: this.import, disabled: !this.canAddAsset },
            {
              title: 'Download template',
              icon: 'fa-file-spreadsheet',
              href: '/risk analysis asset loader template.xlsx',
              download: 'risk analysis asset loader template.xlsx'
            }
          ]
    },
    classes() {
      let captions
      if (this.environmentMode === 'apm')
        captions = this.taxonomyList ? this.taxonomyList.map(x => `${x.category} - ${x.class} - ${x.type}`) : []
      else captions = this.taxonomyList ? this.taxonomyList.map(x => `${x.category} - ${x.class}`) : []

      return captions.sort()
    },
    assetTableLabel() {
      if (!this.value || !this.filteredValue) return 'Assets'

      return this.isFiltered
        ? `Assets (${this.filteredValue.length.toLocaleString()} of ${this.value.length.toLocaleString()})`
        : `Assets (${this.value.length.toLocaleString()})`
    },
    isFiltered() {
      return !(
        (this.filterDef.improve === '' || this.filterDef.improve === undefined || this.filterDef.improve === null) &&
        (this.filterDef.name === '' || this.filterDef.name === undefined || this.filterDef.name === null) &&
        (this.filterDef.class === '' || this.filterDef.class === undefined || this.filterDef.class === null) &&
        (this.filterDef.mtbf === '' || this.filterDef.mtbf === undefined || this.filterDef.mtbf === null) &&
        (this.filterDef.opportunity === '' ||
          this.filterDef.opportunity === undefined ||
          this.filterDef.opportunity == null)
      )
    },
    assetActions() {
      let actions = [{ title: this.readonly ? 'View' : 'Edit', icon: EDIT_ICON, handler: this.edit }]

      if (this.mode === 'analysis') {
        actions.push({
          title: 'Improve',
          icon: 'fa-chart-line',
          handler: this.showImprove,
          disabled: this.improveDisabled
        })
        if (!this.readonly) {
          actions.push({
            title: this.environmentMode === 'apm' ? 'Remove' : 'Delete',
            icon: 'fa-regular fa-trash-can',
            handler: this.delete,
            disabled: this.deleteDisabled
          })
        }
      }

      if (this.environmentMode === 'apm' && this.selectedAsset != null) {
        if (this.selectedAsset.twinId) {
          actions.push({ title: 'Open Twin', icon: this.twinIcon, handler: this.openTwin })
        } else {
          actions.push({ title: 'Open Asset', icon: ASSET_ICON, handler: this.openAsset })
        }
      }
      return actions
    },
    filterCaption() {
      return !this.value ? 'Risk Analyses' : `${this.value.length} Risk Analyses`
    },
    analysisAssets() {
      if (!this.value) return []

      return this.value.filter(
        x =>
          x.probabilityValue !== null &&
          x.probabilityValue !== undefined &&
          !isNaN(x.probabilityValue) &&
          x.consequenceValue !== null &&
          x.consequenceValue !== undefined &&
          !isNaN(x.consequenceValue) &&
          x.risk !== null &&
          x.risk !== undefined &&
          !isNaN(x.risk)
      )
    },
    maxConsequence() {
      if (!this.value) return 0

      let cost = Math.max.apply(
        Math,
        this.analysisAssets.map(x => x.consequenceValue)
      )
      return isNaN(cost) ? 0 : cost
    },
    maxRisk() {
      if (!this.value) return 0

      let risk = Math.max.apply(
        Math,
        this.analysisAssets.map(x => x.risk)
      )
      return isNaN(risk) ? 0 : risk
    }
  },
  methods: {
    assetNameClass(item) {
      return this.environmentMode === 'apm' && !item.twinId ? 'asset-icon' : ''
    },
    twinIconColor(item) {
      if (item.twinStartDate && item.twinMissionTime) {
        const twinStart = DateTime.fromISO(item.twinStartDate, { zone: this.timeZone })
        const twinEndDate = twinStart.plus(Duration.fromISO(item.twinMissionTime)).set({ milliseconds: 0 })
        const analysisEnd = DateTime.fromISO(this.analysisEndDate, { zone: this.timeZone })
        if (twinEndDate < analysisEnd) {
          return 'orange'
        }
        if (analysisEnd < twinStart) {
          return 'red'
        }
      }
      return 'gray'
    },
    twinIconTooltip(item) {
      let message = `This twin does not specify its own start date or mission time.`
      if (item.twinStartDate && item.twinMissionTime) {
        const twinStartDateObj = DateTime.fromISO(item.twinStartDate, { zone: this.timeZone })
        if (this.analysisEndDate) {
          const twinEndDate = twinStartDateObj.plus(Duration.fromISO(item.twinMissionTime)).set({ milliseconds: 0 })
          const analysisEndDateObj = DateTime.fromISO(this.analysisEndDate, { zone: this.timeZone })
          if (analysisEndDateObj < twinStartDateObj) {
            return 'The mission time for this twin begins after the analysis end date.'
          }
          //calc mission time is time between twin start and analysis end
          const calcMissionTime = analysisEndDateObj - twinStartDateObj
          message = `This twin starts on ${twinStartDateObj.toLocaleString(DateTime.DATE_MED)} with a calculated mission time of ${Time.safeToHuman(Duration.fromMillis(calcMissionTime).shiftTo(...Time.getDurationUnits(item.twinMissionTime)))}`
          if (twinEndDate < analysisEndDateObj) {
            message += '<b>, but the twin mission time will end before the analysis mission time.</b>'
          } else if (twinEndDate > analysisEndDateObj) {
            message += ', and the analysis mission time will end before the twin mission time.'
          }
        } else {
          message = `This twin starts on ${twinStartDateObj.toLocaleString(DateTime.DATE_MED)} with a mission time of ${Time.safeToHuman(item.twinMissionTime)}`
        }
      }

      return message
    },
    menuInput(item) {
      if (this.$refs[item.assetId] && !this.$refs[item.assetId].isActive) {
        return
      }
      this.selectedAsset = item
    },
    emptyHandler() {
      this.forceMenuClose()
    },
    forceMenuClose() {
      // workaround for known vuetify bug with menus that launch dialogs
      this.importMenu = false
    },
    clearFilter() {
      this.filterDef = {
        improve: undefined,
        name: '',
        class: undefined,
        mtbf: undefined,
        opportunity: undefined
      }
    },
    clearImprove() {
      this.filterDef.improve = undefined
      this.$refs.improveFilter.reset()
    },
    clearName() {
      this.filterDef.name = ''
      this.$refs.nameFilter.reset()
    },
    clearClass() {
      this.filterDef.class = undefined
      this.$refs.classFilter.reset()
    },
    clearMtbf() {
      this.filterDef.mtbf = undefined
      this.$refs.mtbfFilter.reset()
    },
    clearOpportunity() {
      this.filterDef.opportunity = undefined
      this.$refs.opportunityFilter.reset()
    },
    improveChanged(v) {
      this.filterDef.improve = v
    },
    nameChanged(v) {
      this.filterDef.name = v
    },
    classChanged(v) {
      this.filterDef.class = v
    },
    mtbfChanged(v) {
      this.filterDef.mtbf = v
    },
    opportunityChanged(v) {
      this.filterDef.opportunity = v
    },
    filterAssets() {
      if (!this.value) {
        this.filteredValue = []
        return
      }

      if (!this.isFiltered) {
        this.filteredValue = this.value
        return
      }

      this.filteredValue = this.value.filter(x => {
        return (
          this.improveFilter(x, this.filterDef.improve) &&
          this.nameFilter(x, this.filterDef.name) &&
          this.classFilter(x, this.filterDef.class) &&
          this.mtbfFilter(x, this.filterDef.mtbf) &&
          this.opportunityFilter(x, this.filterDef.opportunity)
        )
      })
    },
    opportunityFilter(item, filter) {
      let result = true
      if (filter !== undefined && filter !== null && filter !== '')
        result =
          !item || !item.probabilityValue || item.probabilityValue === 0
            ? false
            : filter === 'Over opportunity threshold'
              ? item.probabilityValue >= this.probabilityThreshold
              : item.probabilityValue < this.probabilityThreshold

      return result
    },
    mtbfFilter(item, filter) {
      let result = true
      if (filter !== undefined && filter !== null && filter !== '') {
        if (filter === 'No benchmark available') {
          result = !this.taxonomyMap[item.assetClass] || !this.taxonomyMap[item.assetClass].mtbf
        } else {
          result =
            item === null ||
            item === undefined ||
            isNaN(item.probabilityValue) ||
            !this.taxonomyMap ||
            !this.taxonomyMap[item.assetClass]
              ? false
              : filter === 'Worse than benchmark'
                ? Duration.fromISO(item.comparativeMtbf).toMillis() < 0
                : Duration.fromISO(item.comparativeMtbf).toMillis() >= 0
        }
      }
      return result
    },
    classFilter(item, filter) {
      let result = true
      if (filter !== undefined && filter !== null && filter !== '')
        result = !item || !item.assetClassLabel ? false : item.assetClassLabel === filter

      return result
    },
    nameFilter(item, filter) {
      let result = true
      if (filter !== undefined && filter !== null && filter !== '')
        result = !item || !item.assetName ? false : item.assetName.toLowerCase().includes(filter.toLowerCase())

      return result
    },
    improveFilter(item, filter) {
      let result = true
      if (filter !== undefined && filter !== null && filter !== '')
        result = (item.improve === true && filter === 'true') || (item.improve === false && filter === 'false')

      return result
    },
    mtbfDisplayText(item) {
      if (this.environmentMode !== 'ara' && !item.distributionType) {
        return null
      }
      const mtbf =
        item.probabilityFailureDistributionScale ||
        (item.probabilityValue === null || isNaN(item.probabilityValue) ? null : `P${item.probabilityValue}D`)
      if (!mtbf) {
        return null
      }

      const units = this.environmentMode === 'ara' ? ['days'] : Time.getDurationUnits(mtbf)
      let iso = Duration.fromISO(mtbf)
        .shiftTo(...units)
        .toISO()
      const mtbfText = Time.safeToHuman(iso)

      if (this.mode !== 'scenario' || (this.mode === 'scenario' && !item.library)) {
        return mtbfText
      } else {
        const parts = mtbfText.split(' ')
        const mask = parts[0].replace(/\d/g, '#')
        const unit = parts[1]
        return `<span style="color: transparent;text-shadow: 0 0 5px rgba(0,0,0,0.5);">${mask}</span> ${unit}`
      }
    },
    improveText(item) {
      return item.improveDescription ? item.improveDescription : 'No improvement notes specified.'
    },
    calcPof(probabilityValue) {
      return isNaN(probabilityValue) || probabilityValue < 0 ? 0 : probabilityValue
    },
    libraryComparisonColor(item) {
      if (this.mode === 'scenario' && item?.library) return 'accent'

      if (!item || !item.comparativeMtbf) return 'grey lighten-1'

      return Duration.fromISO(item.comparativeMtbf).toMillis() < 0 ? 'amber darken-2' : 'green lighten-1'
    },
    opportunityColor(item) {
      if (!item || !item.probabilityValue || item.probabilityValue === 0) return 'grey lighten-1'

      return item.probabilityValue >= this.probabilityThreshold ? 'green darken-3' : 'grey lighten-1'
    },
    opportunityTooltip(item) {
      if (!item) return 'Opportunity not calculated.'

      if (!item.consequenceValue)
        return `This asset does not specify a consequence value for evaluation of potential opportunity.`
      else {
        if (this.environmentMode === 'apm')
          return `This asset's potential operational cost reduction opportunity is <strong>$${item.consequenceValue.toLocaleString()}</strong>.`
        else
          return `This asset's potential operational cost reduction opportunity is <strong>$${item.consequenceValue.toLocaleString()}</strong>.<br/><br/>To reduce your failure risk with a more effective strategy, click the <em class="fa-solid fa-circle-arrow-up accent--text"></em> <strong>UPGRADE</strong> button above to upgrade to HxGN APM.`
      }
    },
    buildLibraryComparisonText(item) {
      if (!item) {
        return null
      }
      if (this.environmentMode !== 'ara' && !item.distributionType) {
        return null
      }
      if (!item.assetClass || !this.taxonomyMap || !this.taxonomyMap[item.assetClass])
        return `No taxonomy classification assigned.<br/><br/> To compare your observed characteristic life to the library benchmark please select the asset\'s taxonomy.${this.currentMtbfDisplayText(item)}`

      if (!this.taxonomyMap[item.assetClass].mtbf)
        return `No library characteristic life data could be found for the specified taxonomy.${this.currentMtbfDisplayText(item)}`

      if (this.mode === 'scenario' && item.library)
        return `This asset is using the library benchmark characteristic life of <strong><span style="color: transparent;text-shadow: 0 0 5px rgba(0,0,0,0.5);">#,###</span> days</strong>. <br/><br/>To compare this asset's performance to the library benchmark and implement a more effective strategy, click the <em class="fa-solid fa-circle-arrow-up accent--text"></em> <strong>UPGRADE</strong> button above to upgrade to HxGN APM.`

      if (item.comparativeMtbf) {
        if (Duration.fromISO(item.comparativeMtbf).toMillis() < 0) {
          if (this.environmentMode === 'ara')
            return `This asset's characteristic life is <strong>worse</strong> than the library benchmark by <strong><span style="color: transparent;text-shadow: 0 0 5px rgba(0,0,0,0.5);">##</span>%</strong>, a difference of <strong><span style="color: transparent;text-shadow: 0 0 5px rgba(0,0,0,0.5);">#,###</span> days</strong>. <br/><br/>To compare this asset's performance to the library benchmark and implement a more effective strategy, click the <em class="fa-solid fa-circle-arrow-up accent--text"></em> <strong>UPGRADE</strong> button above to upgrade to HxGN APM.`
          else
            return `This asset's characteristic life is <strong>worse</strong> than the library benchmark by a difference of <strong><span>${this.worseMtbfDisplayText(item)}</span></strong>.${this.currentMtbfDisplayText(item)}`
        } else {
          if (this.environmentMode === 'ara')
            return `Keep up the good work! This asset's characteristic life is <strong>better or equal</strong> to the library benchmark of <strong><span style="color: transparent;text-shadow: 0 0 5px rgba(0,0,0,0.5);">#,###</span> days</strong>. <br/><br/>There still may be an opportunity to reduce your total maintenance spending with a more effective strategy,  click the <em class="fa-solid fa-circle-arrow-up accent--text"></em> <strong>UPGRADE</strong> button above to upgrade to HxGN APM.`
          else
            return `Keep up the good work! This asset's characteristic life is <strong>better or equal</strong> to the library benchmark of <strong><span>${this.betterMtbfDisplayText(item)}</span></strong>. <br/><br/>There still may be an opportunity to reduce your total maintenance spending with a more effective strategy.${this.currentMtbfDisplayText(item)}`
        }
      }
    },
    worseMtbfDisplayText(item) {
      if (item.comparativeMtbf) {
        const mtbf =
          item.probabilityFailureDistributionScale ||
          (item.probabilityValue === null || isNaN(item.probabilityValue) ? null : `P${item.probabilityValue}D`)
        const units = Time.getDurationUnits(mtbf)
        let iso = Duration.fromISO(item.comparativeMtbf)
          .negate()
          .shiftTo(...units)
          .toISO()
        return Time.safeToHuman(iso)
      } else {
        return ''
      }
    },
    betterMtbfDisplayText(item) {
      if (!item.assetClass || !this.taxonomyMap[item.assetClass] || !this.taxonomyMap[item.assetClass].mtbf) {
        return ''
      }
      const mtbf =
        item.probabilityFailureDistributionScale ||
        (item.probabilityValue === null || isNaN(item.probabilityValue) ? null : `P${item.probabilityValue}D`)
      const units = Time.getDurationUnits(mtbf)
      let iso = Duration.fromISO(this.taxonomyMap[item.assetClass].mtbf)
        .shiftTo(...units)
        .toISO()
      return Time.safeToHuman(iso)
    },
    currentMtbfDisplayText(item) {
      if (!item.asset?.failureData) {
        return ''
      }
      const mtbf = item.asset?.failureData ? Duration.fromISO(JSON.parse(item.asset.failureData).mtbf).as('days') : null
      return isNaN(mtbf) ? '' : `<br/><br/><span>The measured MTBF is <strong>${Math.round(mtbf)} days</strong></span>`
    },
    async warnAboutInvalidatingScenarios() {
      if (this.mode === 'scenario') return false

      if (!this.scenarios || this.scenarios.length === 0 || this.scenarios.filter(x => x.valid === true).length === 0)
        return false

      if (this.scenarioInvalidationConfirmed) return false

      let confirmChange = await this.$refs.warningDialog.show()

      if (confirmChange.suppress) {
        this.$emit('update:scenarioInvalidationConfirmed', true)
        this.suppress = true
      }

      return !confirmChange.result
    },
    openTwin(item) {
      this.$emit('openTwin', item)
    },
    openAsset(item) {
      this.$emit('openAsset', item)
    },
    async addAsset() {
      if (this.mode === 'scenario') return

      if (this.value.length >= this.assetsPerAnalysis) {
        this.showMessage(
          'error',
          `You have reached your maximum limit of ${this.assetsPerAnalysis} assets for the analysis at this tier - you can easily upgrade to get more!`
        )
        return
      }

      let beforeEditInvalidationState = JSON.parse(JSON.stringify(this.scenarioInvalidationConfirmed))
      if (await this.warnAboutInvalidatingScenarios()) return

      this.$emit('addClick')
      if (this.canAddAsset && this.assetDialog) {
        let result = await this.assetDialog.show({ analysisId: this.analysis.id })
        if (this.suppress) return

        if (!beforeEditInvalidationState && !result) {
          // reset the invalidation confirmation state if it had not been set prior to prompting for editing an asset
          // then they show the dialog but don't make any invalidating changes
          this.$emit('update:scenarioInvalidationConfirmed', false)
        }
      }
    },
    async edit(item, improve) {
      if (!this.assetDialog && !this.editAssetDialog) return

      let beforeEditInvalidationState = null
      if (this.mode === 'analysis') {
        beforeEditInvalidationState = JSON.parse(JSON.stringify(this.scenarioInvalidationConfirmed))
        if (await this.warnAboutInvalidatingScenarios()) return
      }

      let showArgs = {
        asset: item,
        missionParametersDirty: this.missionParametersDirty,
        message: this.missionParametersDirty
          ? {
              type: 'info',
              show: true,
              text: 'There are unsaved changes to the mission parameters that need to be saved before edits can be made to the asset.'
            }
          : null,
        scenarioMode: this.mode === 'scenario',
        improve: improve === true
      }
      if (this.mode === 'scenario') {
        showArgs.originalProbabilityFailureDistributionScale =
          this.comparisonValueDictionary[item.assetId].probabilityFailureDistributionScale
        showArgs.originalConsequenceValue = this.comparisonValueDictionary[item.assetId].consequenceValue
      }

      let result =
        this.environmentMode === 'apm'
          ? await this.editAssetDialog.show(showArgs)
          : await this.assetDialog.show(showArgs)
      if (this.suppress) return

      if (this.mode === 'analysis' && !beforeEditInvalidationState && !result) {
        // reset the invalidation confirmation state if it had not been set prior to prompting for editing an asset
        // then they show the dialog but don't make any invalidating changes
        this.$emit('update:scenarioInvalidationConfirmed', false)
      }
    },
    async delete(item) {
      if (this.mode === 'scenario') return

      let beforeEditInvalidationState = JSON.parse(JSON.stringify(this.scenarioInvalidationConfirmed))
      if (await this.warnAboutInvalidatingScenarios()) {
        return
      }
      let verb = this.environmentMode === 'apm' ? 'Remove' : 'Delete'
      let suffix = this.environmentMode === 'apm' ? ' from this risk analysis' : ''
      let confirmDelete = await this.$root.$confirm(
        verb + ' asset',
        'Are you sure you want to ' + verb.toLowerCase() + ' this asset' + suffix + '?'
      )
      if (!confirmDelete) {
        if (this.suppress) return

        if (!beforeEditInvalidationState) {
          // reset the invalidation confirmation state if it had not been set prior to prompting for editing an asset
          // then they show the dialog but don't make any invalidating changes
          this.$emit('update:scenarioInvalidationConfirmed', false)
        }

        return
      }

      this.$emit('delete', item.assetId)
    },
    deleteDisabled() {
      return !this.canDeleteAsset
    },
    async toggleImprove(item) {
      if (this.mode === 'scenario') return

      item.improve = !item.improve
      this.$emit('improvementToggled', item)
    },
    showImprove(item) {
      this.edit(item, this.mode === 'analysis')
    },
    improveDisabled(asset) {
      return !asset
    },
    characteristicLifeDiffers(asset) {
      if (this.mode !== 'scenario' || !this.comparisonValueDictionary || !this.value) return false

      let v = this.comparisonValueDictionary[asset.assetId]
      if (!v) return false

      const charLife1 = Duration.fromISO(v.probabilityFailureDistributionScale)
      const charLife2 = Duration.fromISO(asset.probabilityFailureDistributionScale)

      return charLife1.valueOf() !== charLife2.valueOf()
    },
    consequenceValueDiffers(asset) {
      if (this.mode !== 'scenario' || !this.comparisonValueDictionary || !this.value) return false

      let v = this.comparisonValueDictionary[asset.assetId]
      if (!v) return false

      let c1 = parseInt(v.consequenceValue)
      let c2 = parseInt(asset.consequenceValue)

      return !this.same(c1, c2)
    },
    hasChanges(asset) {
      return this.characteristicLifeDiffers(asset) || this.consequenceValueDiffers(asset)
    },
    changeSummaryText(asset) {
      let diffCharLife = this.characteristicLifeDiffers(asset)
      let diffCon = this.consequenceValueDiffers(asset)

      let charLife = null
      let conValue = null
      if (this.comparisonValueDictionary[asset.assetId]) {
        charLife = Duration.fromISO(this.comparisonValueDictionary[asset.assetId].probabilityFailureDistributionScale)
        conValue = parseInt(this.comparisonValueDictionary[asset.assetId].consequenceValue)
      }

      const charLifeText = charLife ? `of ${charLife.toHuman()}` : 'is not specified'
      const consText = isNaN(conValue) ? 'is not specified' : `of $${conValue.toLocaleString()}`
      if (diffCharLife && diffCon) return `Base case characteristic life ${charLifeText} and consequence ${consText}.`
      else if (diffCharLife) return `Base case characteristic life ${charLifeText}.`
      else if (diffCon) return `Base case consequence of ${consText}.`
      else return ''
    },
    same(v1, v2) {
      if (isNaN(v1) && isNaN(v2)) return true
      else if ((isNaN(v1) && !isNaN(v2)) || (!isNaN(v1) && isNaN(v2))) return false
      else return v1 == v2
    },
    import() {
      this.forceMenuClose()
      this.$refs.fileInput.$refs.input.click()
    },
    async importFile(file) {
      this.$emit('import', file)
      this.$refs.fileInput.$refs.input.value = null
    },
    showMessage(type, text) {
      let message = {
        type: type,
        text: text,
        show: true
      }
      this.$emit('message', message)
    }
  },
  created() {
    this.debouncedFilterAssets = debounce(this.filterAssets, 100)
  },
  mounted() {
    this.debouncedFilterAssets()
    this.suppress = false
  }
}
</script>
<style lang="scss" scoped>
.v-tooltip__content {
  color: var(--v-primary-base);
  opacity: 0.9 !important;
  border: 1px solid var(--v-primary-base) !important;
  max-width: 400px;
}

.empty-icon {
  padding-left: 10px;
}
.asset-icon {
  padding-left: 25px;
}

.itus-table ::v-deep tbody tr :hover {
  cursor: pointer;
}

.v-file-input {
  display: none;
}
</style>
